import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, Download, Check, Wand2, Sparkles, AlertTriangle, Shuffle,
    Save, MessageSquare, Layers, Image as ImageIcon,
} from "lucide-react";
import {
    fetchSocialPackages, fetchCreativeUsage,
    adConcepts, adGenerate, adList, adGet, adEdit, adRegenerate, adApprove, adSave,
    adListDesigns, adSaveDesign, formatApiError,
} from "@/lib/api";

const FORMAT_LABEL = { story: "Story 1080×1920", square: "Instagram 1080×1080", facebook: "Facebook 1200×630", whatsapp_portrait: "WhatsApp 1080×1350" };
const STATUS_STYLE = { generating: "bg-amber-100 text-amber-700 animate-pulse", done: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-600" };
const STATUS_LABEL = { generating: "Designing…", done: "Done", failed: "Failed" };
const inp = "w-full rounded-xl border border-stone-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60";

function Slider({ label, value, onChange, hint, testid }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <label className="text-[11px] font-black uppercase tracking-wider text-stone-500">{label}</label>
                <span className="text-[11px] font-black text-navy">{value}/10</span>
            </div>
            <input type="range" min="1" max="10" value={value} onChange={(e) => onChange(parseInt(e.target.value))} data-testid={testid} className="w-full accent-saffron" />
            {hint && <p className="text-[10px] text-stone-400">{hint}</p>}
        </div>
    );
}

export function ArtDirectorPanel({ token, onSaved }) {
    const [pkgs, setPkgs] = useState(null);
    const [slug, setSlug] = useState("");
    const [freedom, setFreedom] = useState(6);
    const [brand, setBrand] = useState(7);
    const [density, setDensity] = useState("medium");
    const [concepts, setConcepts] = useState(null);
    const [selected, setSelected] = useState(null);
    const [brief, setBrief] = useState("");
    const [formats, setFormats] = useState(["story", "square", "facebook", "whatsapp_portrait"]);
    const [quality, setQuality] = useState("standard");
    const [poster, setPoster] = useState(null);
    const [history, setHistory] = useState(null);
    const [usage, setUsage] = useState(null);
    const [busy, setBusy] = useState("");
    const [editText, setEditText] = useState({});
    const [designName, setDesignName] = useState("");
    const [designs, setDesigns] = useState([]);
    const pollRef = useRef(null);

    const loadHistory = useCallback(async () => {
        try { const d = await adList(token); setHistory(d.posters); } catch { /* non-fatal */ }
        try { setUsage(await fetchCreativeUsage(token)); } catch { /* non-fatal */ }
        try { setDesigns((await adListDesigns(token)).designs); } catch { /* non-fatal */ }
    }, [token]);

    useEffect(() => {
        fetchSocialPackages(token).then((d) => setPkgs(d.packages)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
        loadHistory();
        return () => clearInterval(pollRef.current);
    }, [token, loadHistory]);

    const startPolling = (id) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const d = await adGet(token, id);
                setPoster(d.poster);
                if (!Object.values(d.poster.formats).some((f) => f.status === "generating")) {
                    clearInterval(pollRef.current); pollRef.current = null; loadHistory();
                }
            } catch { /* retry */ }
        }, 3000);
    };
    useEffect(() => () => clearInterval(pollRef.current), []);

    const getConcepts = async (mode) => {
        if (!slug) return toast.error("Pick a tour package first");
        setBusy("concepts"); setSelected(null);
        try {
            const r = await adConcepts(token, { package_slug: slug, freedom, brand, density, mode });
            setConcepts(r.concepts); setBrief(r.package.brief);
            toast.success(mode === "surprise" ? "The Art Director invented something new" : "Three fresh concepts ready");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const generate = async () => {
        if (!selected) return toast.error("Select a concept first");
        if (!formats.length) return toast.error("Pick at least one format");
        setBusy("generate");
        try {
            const r = await adGenerate(token, { package_slug: slug, concept: selected, formats, quality, freedom, brand, density });
            setPoster(r.poster); toast.success("Art Director is composing your poster(s)");
            startPolling(r.poster.id); loadHistory();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    const act = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); } };
    const doEdit = (fmt) => act(`edit-${fmt}`, async () => {
        const t = (editText[fmt] || "").trim();
        if (!t) return toast.error("Describe the change");
        const r = await adEdit(token, poster.id, { format: fmt, instruction: t });
        setPoster(r.poster); setEditText({ ...editText, [fmt]: "" }); toast.success("Layout updated");
    });
    const doRegen = (fmt, kind) => act(`regen-${fmt}`, async () => {
        const r = await adRegenerate(token, poster.id, { format: fmt, kind });
        setPoster(r.poster); startPolling(poster.id); toast.success(kind === "layout" ? "Re-composing layout" : "Generating a fresh image");
    });
    const doApprove = (fmt, val) => act(`appr-${fmt}`, async () => { const r = await adApprove(token, poster.id, { format: fmt, approved: val }); setPoster(r.poster); });
    const doSave = () => act("save", async () => {
        const r = await adSave(token, poster.id); toast.success("Saved to Content Library"); setPoster(r.poster); loadHistory(); onSaved(r.content);
    });
    const doSaveDesign = (fmt) => act("design", async () => {
        await adSaveDesign(token, poster.id, { name: designName.trim() || selected?.title || "Design", format: fmt });
        toast.success("Design saved to library"); setDesignName(""); loadHistory();
    });

    const approvedCount = poster ? Object.values(poster.formats).filter((f) => f.approved && f.status === "done").length : 0;

    return (
        <div data-testid="art-director">
            <p className="text-sm text-stone-500 mb-4">
                The AI Art Director studies the package, invents original creative concepts, designs a unique layout, paints the photography, and renders your logo, price and QR accurately.
                <span className="ml-2 text-xs font-bold text-stone-400">Images so far: {usage ? usage.total_images : "—"}{usage && <> · Est. <span className="text-navy">${usage.total_est_cost_usd.toFixed(2)}</span></>}</span>
            </p>

            <div className="grid md:grid-cols-2 gap-5">
                <div>
                    <label className="text-xs font-black uppercase tracking-wider text-stone-500">Tour package</label>
                    <select value={slug} onChange={(e) => { setSlug(e.target.value); setConcepts(null); setSelected(null); }} data-testid="ad-package-select" className={`mt-1 ${inp}`}>
                        <option value="">{pkgs ? "Select a package…" : "Loading packages…"}</option>
                        {(pkgs || []).map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                    </select>
                    {brief && <p className="mt-2 rounded-lg bg-cream/60 border border-stone-200 p-2 text-[11px] text-stone-500 whitespace-pre-line" data-testid="ad-brief">{brief}</p>}
                </div>
                <div className="space-y-3">
                    <Slider label="Creative freedom" value={freedom} onChange={setFreedom} testid="ad-freedom" hint={freedom <= 3 ? "Conventional professional ads" : freedom <= 7 ? "Creative, unconventional layouts" : "Experimental, editorial, distinctive"} />
                    <Slider label="Brand consistency" value={brand} onChange={setBrand} testid="ad-brand" hint="How strongly the Ghumne Chaley identity is honoured" />
                    <div>
                        <label className="text-[11px] font-black uppercase tracking-wider text-stone-500">Information density</label>
                        <select value={density} onChange={(e) => setDensity(e.target.value)} data-testid="ad-density" className={`mt-1 ${inp}`}>
                            <option value="low">Low — minimal info</option>
                            <option value="medium">Medium — key facts</option>
                            <option value="high">High — more highlights & detail</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => getConcepts("three")} disabled={busy === "concepts"} data-testid="ad-generate-concepts"
                    className="rounded-full bg-navy text-white font-display font-black text-xs px-5 py-2.5 inline-flex items-center gap-2 hover:bg-saffron disabled:opacity-50">
                    {busy === "concepts" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate three concepts
                </button>
                <button onClick={() => getConcepts("more")} disabled={busy === "concepts"} data-testid="ad-more-concepts"
                    className="rounded-full border border-navy text-navy font-display font-black text-xs px-5 py-2.5 inline-flex items-center gap-2 hover:bg-navy hover:text-white disabled:opacity-50">
                    <RefreshCw className="h-4 w-4" /> Try three more
                </button>
                <button onClick={() => getConcepts("surprise")} disabled={busy === "concepts"} data-testid="ad-surprise"
                    className="rounded-full bg-saffron text-white font-display font-black text-xs px-5 py-2.5 inline-flex items-center gap-2 hover:bg-navy disabled:opacity-50">
                    <Shuffle className="h-4 w-4" /> Surprise me — create something unique
                </button>
            </div>

            {/* concepts */}
            {concepts && (
                <div className="mt-5" data-testid="ad-concepts">
                    <label className="text-xs font-black uppercase tracking-wider text-stone-500">Choose a creative concept</label>
                    <div className="mt-2 grid md:grid-cols-3 gap-3">
                        {concepts.map((c, i) => (
                            <button key={i} onClick={() => setSelected(c)} data-testid={`ad-concept-${i}`}
                                className={`text-left rounded-2xl border p-4 transition-colors ${selected === c ? "border-saffron bg-gold/15 ring-2 ring-saffron/40" : "border-stone-200 bg-white hover:border-saffron/60"}`}>
                                <p className="font-display font-black text-base text-navy">{c.title}</p>
                                <p className="mt-1 text-[12px] text-stone-600 leading-snug">{c.description}</p>
                                <p className="mt-2 text-[11px] text-stone-500 italic leading-snug">{c.storytelling}</p>
                                <div className="mt-2 flex items-center gap-1.5">
                                    {c.palette && Object.values(c.palette).slice(0, 3).map((col, j) => (
                                        <span key={j} className="h-4 w-4 rounded-full border border-stone-200" style={{ background: col }} title={col} />
                                    ))}
                                    <span className="ml-auto text-[10px] font-bold uppercase text-stone-400">{c.typography}</span>
                                </div>
                                <p className="mt-1.5 text-[10px] text-stone-400">{c.layout_concept}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* formats + generate */}
            {selected && (
                <div className="mt-5 rounded-2xl border border-saffron/40 bg-gold/5 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs font-black uppercase text-stone-500">Formats:</span>
                        {Object.entries(FORMAT_LABEL).map(([k, label]) => (
                            <label key={k} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-bold cursor-pointer ${formats.includes(k) ? "border-saffron bg-gold/15 text-navy" : "border-stone-300 text-stone-500"}`} data-testid={`ad-format-${k}`}>
                                <input type="checkbox" className="hidden" checked={formats.includes(k)} onChange={(e) => setFormats(e.target.checked ? [...formats, k] : formats.filter((f) => f !== k))} />
                                {label}
                            </label>
                        ))}
                        <select value={quality} onChange={(e) => setQuality(e.target.value)} data-testid="ad-quality" className="rounded-lg border border-stone-300 px-2 py-1.5 text-xs text-navy">
                            <option value="standard">Standard</option>
                            <option value="high">High</option>
                        </select>
                        <button onClick={generate} disabled={busy === "generate"} data-testid="ad-generate-btn"
                            className="ml-auto rounded-full bg-saffron hover:bg-navy text-white font-display font-black text-sm px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50">
                            {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Design poster
                        </button>
                    </div>
                </div>
            )}

            {/* generated poster gallery */}
            {poster && (
                <div className="mt-6 border-t border-stone-200 pt-5">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <p className="text-sm font-bold text-navy">{poster.package_name} <span className="text-stone-400 font-semibold">· {poster.concept?.title}</span>{poster.est_cost_usd > 0 && <span className="text-stone-400 font-semibold"> · est. ${poster.est_cost_usd.toFixed(3)}</span>}</p>
                        <button onClick={doSave} disabled={!approvedCount || busy === "save" || !!poster.saved_content_id} data-testid="ad-save-btn"
                            className="rounded-full bg-navy hover:bg-saffron text-cream hover:text-white font-display font-black text-xs px-5 py-2.5 disabled:opacity-40 inline-flex items-center gap-2">
                            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            {poster.saved_content_id ? "Saved to Library" : `Save approved (${approvedCount}) to Library`}
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries(poster.formats).map(([fmt, f]) => (
                            <div key={fmt} className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`ad-card-${fmt}`}>
                                <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100">
                                    <p className="text-xs font-black text-navy">{FORMAT_LABEL[fmt]}</p>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${STATUS_STYLE[f.status]}`} data-testid={`ad-status-${fmt}`}>{STATUS_LABEL[f.status]}{f.approved ? " · Approved" : ""}</span>
                                </div>
                                <div className={`bg-stone-100 flex items-center justify-center ${fmt === "story" ? "h-72" : fmt === "facebook" ? "h-32" : "h-56"}`}>
                                    {f.status === "generating" && <span className="inline-flex flex-col items-center gap-2 text-stone-400"><Loader2 className="h-6 w-6 animate-spin text-saffron" /><span className="text-xs font-bold">Composing…</span></span>}
                                    {f.status === "failed" && <span className="px-4 text-center text-xs font-semibold text-red-600 flex items-start gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" />{f.error || "Failed"}</span>}
                                    {f.status === "done" && f.card_url && <img src={`${f.card_url}?v=${poster.updated_at}`} alt="" className="h-full w-full object-contain bg-black/90" data-testid={`ad-image-${fmt}`} />}
                                </div>
                                {/* AI edit box */}
                                {f.status === "done" && !poster.saved_content_id && (
                                    <div className="px-3 py-2 border-t border-stone-100">
                                        <div className="flex gap-1.5">
                                            <input value={editText[fmt] || ""} onChange={(e) => setEditText({ ...editText, [fmt]: e.target.value })} data-testid={`ad-edit-input-${fmt}`}
                                                placeholder='Edit with AI — "make the price prominent", "vintage poster"…'
                                                className="flex-1 rounded-lg border border-stone-300 px-2.5 py-1.5 text-[11px] text-navy" />
                                            <button onClick={() => doEdit(fmt)} disabled={busy === `edit-${fmt}`} data-testid={`ad-edit-apply-${fmt}`} title="Apply AI edit"
                                                className="shrink-0 rounded-lg bg-navy text-white text-[10px] font-black uppercase px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
                                                {busy === `edit-${fmt}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-1.5 px-3 py-2.5 border-t border-stone-100">
                                    <button onClick={() => doRegen(fmt, "layout")} disabled={f.status === "generating" || !!poster.saved_content_id} data-testid={`ad-relayout-${fmt}`} title="New layout, same photo"
                                        className="rounded-full border border-stone-300 text-stone-600 hover:border-saffron hover:text-navy text-[10px] font-black uppercase px-2 py-1.5 inline-flex items-center gap-1 disabled:opacity-40"><Layers className="h-3 w-3" /> Layout</button>
                                    <button onClick={() => doRegen(fmt, "image")} disabled={f.status === "generating" || !!poster.saved_content_id} data-testid={`ad-reimage-${fmt}`} title="Fresh AI image + layout"
                                        className="rounded-full border border-stone-300 text-stone-600 hover:border-saffron hover:text-navy text-[10px] font-black uppercase px-2 py-1.5 inline-flex items-center gap-1 disabled:opacity-40"><ImageIcon className="h-3 w-3" /> Image</button>
                                    {f.status === "done" && <a href={`${f.card_url}?v=${poster.updated_at}`} download={`gc-${poster.package_slug}-${fmt}.jpg`} data-testid={`ad-download-${fmt}`}
                                        className="rounded-full border border-stone-300 text-stone-600 hover:border-saffron hover:text-navy text-[10px] font-black uppercase px-2 py-1.5 inline-flex items-center gap-1"><Download className="h-3 w-3" /></a>}
                                    {f.status === "done" && <button onClick={() => doSaveDesign(fmt)} title="Save design" data-testid={`ad-savedesign-${fmt}`}
                                        className="rounded-full border border-stone-300 text-stone-600 hover:border-saffron hover:text-navy text-[10px] font-black uppercase px-2 py-1.5 inline-flex items-center gap-1"><Save className="h-3 w-3" /></button>}
                                    <button onClick={() => doApprove(fmt, !f.approved)} disabled={f.status !== "done" || !!poster.saved_content_id} data-testid={`ad-approve-${fmt}`}
                                        className={`ml-auto rounded-full text-[10px] font-black uppercase px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40 ${f.approved ? "bg-green-600 text-white" : "border border-green-600 text-green-700 hover:bg-green-50"}`}>
                                        <Check className="h-3 w-3" /> {f.approved ? "Approved" : "Approve"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* design library + history */}
            {(designs.length > 0 || (history && history.length > 0)) && (
                <div className="mt-6 border-t border-stone-200 pt-4 grid md:grid-cols-2 gap-5">
                    {designs.length > 0 && (
                        <div>
                            <p className="text-xs font-black uppercase tracking-wider text-stone-500 mb-2">Design library</p>
                            <div className="space-y-1.5" data-testid="ad-design-library">
                                {designs.map((d) => (
                                    <div key={d.id} className="rounded-xl border border-stone-200 bg-white px-3.5 py-2 text-xs flex items-center justify-between gap-2" data-testid={`ad-design-${d.id}`}>
                                        <span className="font-bold text-navy truncate">{d.name}</span>
                                        <span className="text-stone-400 font-mono text-[10px]">{d.signature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {history && history.length > 0 && (
                        <div>
                            <p className="text-xs font-black uppercase tracking-wider text-stone-500 mb-2">Recent AI posters</p>
                            <div className="space-y-1.5" data-testid="ad-history">
                                {history.map((h) => (
                                    <button key={h.id} onClick={() => setPoster(h)} data-testid={`ad-history-${h.id}`}
                                        className={`w-full text-left rounded-xl border px-3.5 py-2 text-xs flex items-center justify-between gap-2 ${poster?.id === h.id ? "border-saffron bg-gold/10" : "border-stone-200 bg-white hover:border-saffron/60"}`}>
                                        <span className="font-bold text-navy truncate">{h.package_name} <span className="text-stone-400 font-semibold">— {h.concept?.title}</span></span>
                                        <span className="flex gap-1 shrink-0">
                                            {Object.entries(h.formats).map(([fmt, f]) => <span key={fmt} className={`rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase ${STATUS_STYLE[f.status]}`}>{fmt}{f.approved ? " ✓" : ""}</span>)}
                                            {h.saved_content_id && <span className="rounded-full bg-navy text-cream px-1.5 py-0.5 text-[9px] font-black uppercase">In library</span>}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
